/**
 * FOLLOWER BOOST — Hero V5.2 shared helpers (imported by fb-hero-v52.js). Original header:
 * FOLLOWER BOOST — Hero V5.2 controller (finition cinématique)
 *
 * Scroll progress p (0..1 of the pinned runway) drives everything; every value is a pure function
 * of p, so the scene plays backwards when scrolling up. Three depth planes:
 *   1. network logos (first plane): emerge from the phone screen in three waves,
 *   2. service pictograms + short badges (second plane): appear around the logos,
 *   3. atmosphere: pre-rendered photoreal video (canvas image sequence) + light trails, emission
 *      pulses and sparks drawn on a second canvas.
 * Choreography:
 *   0–12 %   impact: phone, beams, first wave (Instagram, TikTok, YouTube) already in the air
 *   12–30 %  activation: the screen lights up, pulses, wave 2 emerges from the screen
 *   30–50 %  ascension: wave 3 emerges, all networks rise to their own slots (no overlap)
 *   50–70 %  services: Likes, Vues, Followers… appear around the logos, few labels at a time
 *   70–80 %  preparation: the constellation tightens, the screen charges
 *   80–92 %  explosion: light wave from the screen, logos fly toward the viewer (front plane),
 *            services follow on the second plane, trails and sparks
 *   92–95 %  final composition held
 *   95–100 % conclusion: badges disperse, logos line up at the bottom like the catalogue grid
 * The composition is solved once per layout (pairwise repulsion inside the free area, phone and
 * copy excluded), so large logos never overlap and the phone always stays visible.
 */
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const DESKTOP = window.matchMedia('(min-width: 990px)');
const LOW = (navigator.connection && navigator.connection.saveData) || (navigator.deviceMemory && navigator.deviceMemory < 2);
const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
const lerp = (a, b, t) => a + (b - a) * t;
const smooth = (t) => t * t * (3 - 2 * t);
const easeOut3 = (t) => 1 - Math.pow(1 - t, 3);
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const span = (p, a, b) => clamp((p - a) / Math.max(1e-6, b - a));

/* wave of each network (0 = visible from the first frame) and its brand glow */
const WAVE = { instagram: 0, tiktok: 0, youtube: 0, facebook: 1, snapchat: 1, x: 1, twitch: 1, linkedin: 2, spotify: 2, discord: 2, telegram: 2, whatsapp: 2, vimeo: 2 };
const NET_GLOW = { instagram: '#ff2d87', tiktok: '#25f4ee', youtube: '#ff3d3d', snapchat: '#fffc00', facebook: '#4d8bff', twitch: '#9146ff', x: '#e8f0ff', spotify: '#1ed760', linkedin: '#5aa8ff', discord: '#8b95ff', telegram: '#2aabee', whatsapp: '#25d366', vimeo: '#1ab7ea' };
/* real service families of the catalogue (sprite key, label) */
/* V8: second plane in the brand palette (lime / pale lime) — the multicolour badges read as a firework */
const SERVICES = [
  ['heart', 'Likes', '#b7ff3c'], ['eye', 'Vues', '#d4ff8a'], ['user', 'Followers', '#b7ff3c'], ['film', 'Reels', '#d4ff8a'],
  ['bubble', 'Commentaires', '#b7ff3c'], ['camera', 'Stories', '#d4ff8a'], ['share', 'Partages', '#b7ff3c'], ['users', 'Membres', '#d4ff8a'], ['note', 'Écoutes', '#b7ff3c'],
];

function prng(seed) { let s = seed >>> 0; return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296); }
function parsePairs(str, fallback) {
  const out = String(str || '').split(',').map((s) => s.split(':').map(Number)).filter((a) => a.length === 2 && a.every(Number.isFinite));
  return out.length >= 2 ? out.sort((a, b) => a[0] - b[0]) : fallback;
}
function piecewise(pairs, x) {
  if (x <= pairs[0][0]) return pairs[0][1];
  for (let i = 1; i < pairs.length; i++) {
    const [x1, y1] = pairs[i];
    const [x0, y0] = pairs[i - 1];
    if (x <= x1) return lerp(y0, y1, (x - x0) / Math.max(1e-6, x1 - x0));
  }
  return pairs[pairs.length - 1][1];
}
function parseScreen(str, fallback) {
  const out = String(str || '').split(';').map((s) => s.split(':').map(Number)).filter((a) => a.length === 3 && a.every(Number.isFinite));
  return out.length ? out.sort((a, b) => a[0] - b[0]) : fallback;
}
function screenAt(list, t) {
  if (t <= list[0][0]) return [list[0][1], list[0][2]];
  for (let i = 1; i < list.length; i++) {
    if (t <= list[i][0]) { const k = (t - list[i - 1][0]) / Math.max(1e-6, list[i][0] - list[i - 1][0]); return [lerp(list[i - 1][1], list[i][1], k), lerp(list[i - 1][2], list[i][2], k)]; }
  }
  const l = list[list.length - 1];
  return [l[1], l[2]];
}
const bez = (a, c, b, t) => (1 - t) * (1 - t) * a + 2 * (1 - t) * t * c + t * t * b;

let spritesPromise = null;
function loadSprites(root) {
  if (spritesPromise) return spritesPromise;
  const node = root.querySelector('[data-fb-burst-sprites]') || document.querySelector('[data-fb-burst-sprites]');
  let inline = {};
  try { inline = JSON.parse(node?.textContent || '{}'); } catch { /* noop */ }
  const src = node?.dataset.src;
  spritesPromise = src
    ? fetch(src, { credentials: 'omit' }).then((r) => (r.ok ? r.json() : {})).then((m) => ({ ...inline, ...m })).catch(() => inline)
    : Promise.resolve(inline);
  return spritesPromise;
}

/* Spread circles inside a box: pairwise repulsion + pull to their seed + exclusion rectangles. */
function solve(nodes, box, excl, iters = 260) {
  for (const n of nodes) if (n.sx === undefined) { n.sx = n.x; n.sy = n.y; }
  for (let it = 0; it < iters; it++) {
    for (let i = 0; i < nodes.length; i++) {
      const a = nodes[i];
      for (let j = i + 1; j < nodes.length; j++) {
        const b = nodes[j];
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let d = Math.hypot(dx, dy);
        const min = a.r + b.r + (a.gap + b.gap) / 2;
        if (d < min) {
          if (d < 0.01) { dx = 0.5 - ((i * 7 + j * 3) % 10) / 10; dy = 0.5; d = Math.hypot(dx, dy); }
          const push = (min - d) / d;
          const wa = b.w / (a.w + b.w);
          const wb = a.w / (a.w + b.w);
          a.x -= dx * push * wa; a.y -= dy * push * wa;
          b.x += dx * push * wb; b.y += dy * push * wb;
        }
      }
    }
    for (const n of nodes) {
      n.x += (n.sx - n.x) * 0.03;
      n.y += (n.sy - n.y) * 0.03;
      for (const e of excl) {
        // push the centre out of the exclusion rectangle (through the closest side)
        if (n.x > e.l - n.r * e.k && n.x < e.r + n.r * e.k && n.y > e.t - n.r * e.k && n.y < e.b + n.r * e.k) {
          const opts = [[e.l - n.r * e.k - n.x, 0], [e.r + n.r * e.k - n.x, 0], [0, e.t - n.r * e.k - n.y], [0, e.b + n.r * e.k - n.y]].filter((o, k) => !(e.sides && !e.sides.includes(k)));
          opts.sort((u, v) => Math.hypot(...u) - Math.hypot(...v));
          if (opts[0]) { n.x += opts[0][0]; n.y += opts[0][1]; }
        }
      }
      const ox = n.out || 0;
      n.x = clamp(n.x, box.L + n.r - ox, box.R - n.r + ox);
      n.y = clamp(n.y, box.T + n.r - ox, box.B - n.r);
    }
  }
  return nodes;
}

export { REDUCED, DESKTOP, LOW, clamp, lerp, smooth, easeOut3, easeInOut, span, WAVE, NET_GLOW, SERVICES, prng, parsePairs, piecewise, parseScreen, screenAt, bez, loadSprites, solve };
